import { type VariantProps } from 'class-variance-authority';
import React from 'react';
import { cn } from '@utils';
import { Spinner } from '@atoms/Spinner/Spinner';
import { buttonStyles } from './ButtonVariants';

export type ButtonProps = VariantProps<typeof buttonStyles> &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    loading?: boolean;
    iconLeft?: React.ReactNode;
    iconRight?: React.ReactNode;
    iconClassName?: string;
    label?: string | React.ReactNode;
    isExternalLink?: boolean;
    href?: string;
    openInNewTab?: boolean;
  };

interface IconProps {
  icon: React.ReactNode;
  variant?: ButtonProps['variant'];
  iconClassName?: string;
}

export const Icon = ({ icon, variant, iconClassName }: IconProps) => {
  const svgColor = (variant: ButtonProps['variant'] = 'primary') => {
    switch (variant) {
      case 'primary':
        return 'fill-text-contrast';
      case 'secondary':
        return 'fill-text-dark';
      case 'outlined':
        return 'fill-text-dark';
    }
  };

  const className = cn(
    svgColor(variant),
    'flex h-5 w-5 items-center justify-center [&_svg]:h-full [&_svg]:w-full',
    iconClassName,
  );

  if (!icon) {
    return null;
  }

  return <div className={className}>{icon}</div>;
};

export const Button: React.FC<ButtonProps> = ({
  label,
  variant,
  loading,
  disabled,
  className,
  iconLeft,
  iconRight,
  onClick,
  isExternalLink,
  href,
  openInNewTab,
  iconClassName,
  ...props
}: ButtonProps) => {
  const buttonClassName = cn(
    buttonStyles({ variant }),
    className,
    { 'cursor-not-allowed opacity-80': disabled || loading },
    disabled
      ? 'bg-gray-300 !text-gray-800 hover:!bg-gray-300 focus:!bg-gray-300 active:!border-none active:!bg-gray-300'
      : '',
  );

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (isExternalLink && href !== undefined) {
      if (openInNewTab) {
        window.open(href, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = href;
      }
    }
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <button
      disabled={disabled || loading}
      className={buttonClassName}
      type="button"
      onClick={handleClick}
      tabIndex={disabled || loading ? -1 : 0}
      {...props}
    >
      <div
        className={`flex w-full items-center ${loading ? 'justify-center' : 'justify-between'} gap-2`}
      >
        {loading ? (
          <Spinner />
        ) : (
          <>
            <Icon
              icon={iconLeft}
              variant={variant}
              iconClassName={iconClassName}
            />
            <span className="text-bodyLg flex items-center justify-center whitespace-nowrap font-bold">
              {label}
            </span>
            <Icon
              icon={iconRight}
              variant={variant}
              iconClassName={iconClassName}
            />
          </>
        )}
      </div>
    </button>
  );
};
